import { getPlaceholderAvatar } from '@/core/avatar';
import { execute, getLinkSessionId } from '@/graphql/execute';
import type { UserOrganizationRole } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { getBatch, getBatches } from '@/queries/attachment.query';
import {
  Alarm,
  ArrowUpCircleSolid,
  Calendar,
  CheckCircleSolid,
  ClockCircleSolid,
  DangerCircleSolid,
  FlaskSolid,
} from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ListChecks, ListX, Loader, Loader2, Trash2, X } from 'lucide-react';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Duration } from './Duration';
import { BulkDeleteDialog } from './BulkDeleteDialog';
import { BulkDeletionStatus } from './BulkDeletionStatus';
import PortalTooltip from './PortalTooltip';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

/** `nowMs` pins "now" — pass a ticking clock where the result must advance
 *  while nothing else re-renders (a memoising compiler caches the call on its
 *  arguments, so a hidden `dayjs()` would freeze). Omitted = the real now. */
export const formatDateTime = (date: Date, t?: (key: string, options?: any) => string, nowMs?: number) => {
  const now = dayjs(nowMs);
  if (dayjs(date).isBefore(now.subtract(1, 'week'))) {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
  }

  const dateObj = dayjs(date);
  const diffMinutes = now.diff(dateObj, 'minute');
  if (diffMinutes < -600) {
    return dateObj.format('DD/MM/YYYY [at] HH:mm');
  } else if (diffMinutes < -60) {
    return dateObj.format('dddd [at] HH:mm');
  } else if (diffMinutes < 0) {
    return t ? t('batchList.inMinutes', { count: Math.abs(diffMinutes) }) : `In ${Math.abs(diffMinutes)} minutes`;
  } else if (diffMinutes < 60) {
    return t ? t('batchList.minutesAgo', { count: diffMinutes }) : `${diffMinutes} minutes ago`;
  } else {
    return dateObj.format('dddd [at] HH:mm');
  }
};

export const batchStatusMap = {
  UPLOADING: {
    label: 'Uploading',
    className: 'bg-yellow-500',
    icon: <ArrowUpCircleSolid className='text-slate-400' />,
  },
  WAITING: {
    label: 'Waiting',
    className: 'bg-gray-500 text-white',
    icon: <ClockCircleSolid className='text-amber-500' />,
  },
  PROCESSING: {
    label: 'Processing',
    className: 'bg-blue-500 text-white',
    icon: <Loader2 className='animate-spin' />,
  },
  PROCESSED: {
    label: 'Processed',
    className: 'bg-green-500 text-white',
    icon: <CheckCircleSolid className='text-emerald-500' />,
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-500 text-white',
    icon: <DangerCircleSolid className='text-rose-500' />,
  },
};

interface iBatchListProps {}

const BatchList: FunctionComponent<iBatchListProps> = () => {
  const { t } = useTranslation('components');
  const { t: tp } = useTranslation('project');
  const { hasRole } = useOrganizations();
  const canBulkDelete = hasRole('EDITOR' as UserOrganizationRole) && !getLinkSessionId();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { data: batchesData, isLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => execute(getBatches, { projectId: projectId! }),
    // Batch details open in a drawer over this list, so the list no longer
    // remounts (and refetches) when the user comes back to it.
    refetchInterval: (query) =>
      query.state.data?.batches?.some((b) => b.status === 'PROCESSING' || b.status === 'UPLOADING') ? 5000 : false,
  });

  // Show all batches (UPLOAD and ANALYSIS). ANALYSIS batches are synthetic
  // batches created by deviation, ground-classify, and clipbox-export flows
  // — they have no attachments, only byproducts. They still consume quota
  // and need to be deletable, so we render them with a different badge.
  const batches = batchesData?.batches;
  useEffect(() => {
    if (!batches) return;
    const eligible = new Set(batches.filter((batch) => !batch.bulkDeleteBlockedReason).map((batch) => batch.id));
    setSelectedBatchIds((current) => {
      const next = new Set([...current].filter((id) => eligible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [batches]);
  const stopSelecting = () => {
    setSelectionMode(false);
    setSelectedBatchIds(new Set());
  };
  const toggleBatchSelection = (id: string) =>
    setSelectedBatchIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isLoading) return <Loader className='animate-spin' />;
  return (
    <>
      <div className='flex flex-wrap items-center justify-between gap-x-2 gap-y-1'>
        <Label>{t('batchList.batches')}</Label>
        {canBulkDelete &&
          (!selectionMode ? (
            <Button
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs text-slate-600 dark:text-stone-300'
              onClick={() => setSelectionMode(true)}
            >
              <ListChecks className='size-3.5' />
              {tp('bulkDelete.selectBatches')}
            </Button>
          ) : (
            <div className='flex min-w-0 items-center gap-1'>
              <Button
                variant='ghost'
                size='sm'
                className='h-7 shrink-0 px-2 text-xs'
                onClick={() =>
                  setSelectedBatchIds(
                    new Set(batches?.filter((batch) => !batch.bulkDeleteBlockedReason).map((batch) => batch.id)),
                  )
                }
              >
                {tp('bulkDelete.selectAll')}
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='size-7 shrink-0'
                aria-label={tp('bulkDelete.clearSelection')}
                title={tp('bulkDelete.clearSelection')}
                disabled={!selectedBatchIds.size}
                onClick={() => setSelectedBatchIds(new Set())}
              >
                <ListX className='size-4' />
              </Button>
              <Button
                variant='destructive'
                size='sm'
                className='h-7 min-w-0 px-2 text-xs'
                title={tp('bulkDelete.deleteSelected', { count: selectedBatchIds.size })}
                disabled={selectedBatchIds.size === 0}
                onClick={() => setBulkDialogOpen(true)}
              >
                <Trash2 className='size-3.5' />
                <span className='min-w-0 truncate'>
                  {tp('bulkDelete.deleteSelected', { count: selectedBatchIds.size })}
                </span>
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='size-7 shrink-0'
                aria-label={tp('bulkDelete.cancel')}
                title={tp('bulkDelete.cancel')}
                onClick={stopSelecting}
              >
                <X className='size-4' />
              </Button>
            </div>
          ))}
      </div>
      <div className='flex flex-col border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 mt-2 overflow-hidden'>
        {batches?.length === 0 && (
          <div className='flex flex-col items-center justify-center py-4'>
            <span className='text-slate-500 dark:text-stone-200 text-sm font-medium'>{t('batchList.noBatches')}</span>
            <p className='text-slate-500/75 dark:text-stone-400 text-xs font-medium'>
              {t('batchList.noBatchesDescription')}
            </p>
          </div>
        )}
        {batches?.map((batch) => {
          const batchTitle = batch.label || t('batchList.batchKey', { key: batch.key.toUpperCase() });
          const content = (
            <>
              <div className='flex w-full min-w-0 flex-1 flex-col justify-center gap-1 sm:w-auto'>
                <div className='flex min-w-0 items-center gap-1'>
                  <PortalTooltip id={`batch-status-${batch.id}`}>
                    {t(`batchList.status${batch.status.charAt(0) + batch.status.slice(1).toLowerCase()}`)}
                  </PortalTooltip>
                  <div
                    className='w-5 shrink-0 flex items-center justify-center [&>svg]:size-5'
                    data-tooltip-id={`batch-status-${batch.id}`}
                  >
                    {batchStatusMap[batch.status].icon}
                  </div>
                  {batch.kind === 'ANALYSIS' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          role='img'
                          aria-label={t('batchList.kindAnalysis')}
                          tabIndex={0}
                          className='inline-flex shrink-0 rounded-sm text-violet-600 outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:text-violet-400'
                        >
                          <FlaskSolid className='size-3.5' aria-hidden />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t('batchList.kindAnalysis')}</TooltipContent>
                    </Tooltip>
                  )}
                  <span
                    className='min-w-0 flex-1 truncate text-left font-medium text-md text-slate-900 dark:text-stone-100'
                    title={batchTitle}
                  >
                    {batchTitle}
                  </span>
                </div>
                <span className='flex flex-wrap items-center gap-x-1.5 gap-y-1 pl-6 text-left text-xs leading-5 text-muted-foreground'>
                  {t('batchList.uploadedBy')}{' '}
                  <span className='inline-flex min-w-0 items-center gap-1 text-foreground'>
                    <img
                      src={
                        batch.createdBy?.avatar?.url ||
                        getPlaceholderAvatar(batch.createdBy?.email || batch.createdBy?.id || '')
                      }
                      alt={batch.createdBy?.name || t('batchList.unknown')}
                      className='size-4 rounded-full'
                    />
                    {batch.createdBy?.name || t('batchList.unknown')}
                  </span>
                </span>
              </div>
              <div className='flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 sm:contents'>
                <div className='text-xs text-muted-foreground sm:flex sm:w-32 sm:shrink-0 sm:items-center sm:justify-center'>
                  {batch.kind === 'ANALYSIS'
                    ? t('batchList.noFiles')
                    : t('batchList.files', { count: batch.numberOfAttachments })}
                </div>
                <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground sm:w-42 sm:shrink-0 sm:flex-col sm:items-start sm:justify-center sm:px-4'>
                  <div className='flex items-center gap-1'>
                    <Calendar className='size-4 stroke-2' />
                    {formatDateTime(batch.createdAt, t)}
                  </div>
                  <div className='flex items-center gap-1'>
                    <Alarm className='size-4 stroke-2' />
                    {batch.processingStartedAt ? (
                      <Duration startedAt={batch.processingStartedAt} completedAt={batch.processingCompletedAt} />
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          );
          const rowClass =
            'relative flex flex-col gap-2 border-b border-slate-200/70 last:border-b-0 px-3 py-3 sm:flex-row dark:border-stone-800 hover:bg-slate-50 dark:hover:bg-stone-800';
          if (selectionMode)
            return (
              <div key={batch.id} className={`${rowClass} pl-9 sm:pl-3`}>
                <Checkbox
                  aria-label={tp('bulkDelete.selectBatch', { name: batch.label || batch.key })}
                  className='absolute left-3 top-4 sm:static sm:self-center'
                  checked={selectedBatchIds.has(batch.id)}
                  disabled={!!batch.bulkDeleteBlockedReason}
                  title={
                    batch.bulkDeleteBlockedReason
                      ? tp(
                          batch.bulkDeleteBlockedReason === 'ACTIVE_JOB'
                            ? 'bulkDelete.activeJob'
                            : 'bulkDelete.activeStatus',
                        )
                      : undefined
                  }
                  onCheckedChange={() => toggleBatchSelection(batch.id)}
                />
                {content}
                {batch.bulkDeleteBlockedReason && (
                  <span className='self-center text-xs text-amber-700 dark:text-amber-300'>
                    {tp(
                      batch.bulkDeleteBlockedReason === 'ACTIVE_JOB'
                        ? 'bulkDelete.activeJob'
                        : 'bulkDelete.activeStatus',
                    )}
                  </span>
                )}
              </div>
            );
          return (
            <Link
              key={batch.id}
              className={rowClass}
              to={`/projects/${projectId}/datacenter?batch=${batch.id}`}
              onMouseEnter={() => {
                clearTimeout(prefetchTimeout.current);
                prefetchTimeout.current = setTimeout(
                  () =>
                    queryClient.prefetchQuery({
                      queryKey: ['batch', batch.id],
                      queryFn: () => execute(getBatch, { id: batch.id }),
                      staleTime: 30_000,
                    }),
                  50,
                );
              }}
              onMouseLeave={() => clearTimeout(prefetchTimeout.current)}
            >
              {content}
            </Link>
          );
        })}
      </div>
      {canBulkDelete && projectId && <BulkDeletionStatus projectId={projectId} />}
      {projectId && (
        <BulkDeleteDialog
          projectId={projectId}
          kind='BATCHES'
          selectedIds={[...selectedBatchIds]}
          open={bulkDialogOpen}
          onOpenChange={setBulkDialogOpen}
          onComplete={stopSelecting}
        />
      )}
    </>
  );
};

export default BatchList;
